import { MenuIcon } from './icons';
import { ReactNode, Ref } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { BrandLogo } from './BrandLogo';
import {
  collectionCapacityLabel,
  CollectionCapacitySummary,
} from './collectionContext';

export function CollectionTopBar({
  role,
  username,
  phoneLayout,
  lifecycle = 'active',
  capacity,
  onMore,
  moreOpen,
  moreButtonRef,
  activityLabel,
  onActivity,
  embedded = false,
  headerAction,
}: {
  role?: string;
  username?: string;
  phoneLayout?: boolean;
  lifecycle?: string;
  capacity?: CollectionCapacitySummary;
  onMore?: () => void;
  moreOpen?: boolean;
  moreButtonRef?: Ref<HTMLButtonElement>;
  activityLabel?: string;
  onActivity?: () => void;
  embedded?: boolean;
  headerAction?: ReactNode;
}) {
  const stateLabel = lifecycle === 'deleting' ? 'Deleting' : undefined;
  const capacityLabel = collectionCapacityLabel(capacity);

  return (
    <Paper
      component="header"
      square
      elevation={0}
      sx={{
        position: embedded ? 'static' : 'sticky',
        top: embedded ? 'auto' : 0,
        zIndex: (theme) => theme.zIndex.appBar,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minHeight: 64,
        px: { xs: 0.5, sm: 1 },
        pt: 'env(safe-area-inset-top)',
        borderBottom: 0,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <BrandLogo sx={{ fontSize: { xs: 24, sm: 26 }, flexShrink: 0, mr: 1 }} />
      <Box
        title={[username ? `@${username}` : undefined, role, capacityLabel]
          .filter(Boolean)
          .join(' · ')}
        aria-description={[
          username ? `@${username}` : undefined,
          role,
          capacityLabel,
        ]
          .filter(Boolean)
          .join(' · ')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
          flex: '0 1 auto',
          maxWidth: 240,
          mr: 'auto',
          color: 'text.primary',
          textAlign: 'left',
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: stateLabel ? 'flex' : 'none',
              alignItems: 'center',
              gap: 0.75,
              minWidth: 0,
            }}
          >
            {phoneLayout && username && (
              <Typography
                component="bdi"
                dir="ltr"
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                noWrap
              >
                @{username} ·
              </Typography>
            )}
            {role && (
              <Typography
                component="bdi"
                dir="ltr"
                variant="caption"
                color="text.secondary"
              >
                {role}
              </Typography>
            )}
            {stateLabel && (
              <Typography
                component="bdi"
                dir="ltr"
                variant="caption"
                color="text.secondary"
              >
                · {stateLabel}
              </Typography>
            )}
            {capacityLabel && (
              <Typography
                component="bdi"
                dir="ltr"
                variant="caption"
                color="text.secondary"
                noWrap
              >
                · {capacityLabel}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      {headerAction}
      {(activityLabel || capacity?.status === 'loading') && (
        <Tooltip title={activityLabel || 'Refreshing collection status'}>
          <span>
            <IconButton
              aria-label={activityLabel || 'Refreshing collection status'}
              disabled={!onActivity}
              onClick={onActivity}
            >
              <CircularProgress
                size={20}
                color="inherit"
                aria-label={activityLabel || 'Refreshing collection status'}
              />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {onMore && (
        <IconButton
          ref={moreButtonRef}
          aria-label="Menu"
          aria-haspopup="dialog"
          aria-expanded={Boolean(moreOpen)}
          onClick={onMore}
          sx={{ flexShrink: 0, minWidth: 44, minHeight: 44 }}
        >
          <MenuIcon />
        </IconButton>
      )}
    </Paper>
  );
}
